import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { authLog, authTrail, fingerprint } from 'src/sentry';

import { requestFromApp } from 'src/utils/post-to-app';

import { useAuth } from './use-auth';
import { useUser } from './use-user';

export interface ApiInstance extends AxiosInstance {
  /**
   * Posts one or more events sequentially to v1/events/. Every event names its flow explicitly.
   * `metadata` is included in the body only when given; callers that omit it send today's shape.
   */
  event: (event: string | string[], flow: string, metadata?: Record<string, unknown>) => Promise<void>;
  /** True on routes under /guest — guest requests use the guest API key and skip 401 clearing. */
  isGuest: boolean;
}

const noAuthorization = [
  'auth/login/',
  'auth/registration/',
  'v1/projects/',
  'auth/password/reset/',
  'auth/password/reset/confirm/',
  'login/magic/',
  'login/request-magic-link/',
];

const useAxios = (): ApiInstance => {
  const { auth, setAuth, clearedToken } = useAuth();
  const { lang } = useUser();

  const location = useLocation();
  const isGuest = location.pathname.startsWith('/guest');

  return useMemo(() => {
    const apiInstance = axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    }) as ApiInstance;

    apiInstance.interceptors.request.use(
      (config) => {
        if (!config.headers.Authorization) {
          // URL/method and whether auth is present; never the raw token
          authTrail('request', { url: config.url, method: config.method, hasAuth: !!auth, isGuest });
          if (isGuest && !auth) {
            config.headers.Authorization = `Token ${import.meta.env.VITE_GUEST_API_KEY}`;
          } else {
            config.headers.Authorization = `Token ${auth}`;
          }
        }
        if (noAuthorization.includes(config.url as string)) {
          config.headers.Authorization = null;
        }
        config.headers['Accept-Language'] = lang;
        return config;
      },
      (error) => Promise.reject(error)
    );

    apiInstance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        if (error?.response?.status === 401 && !isGuest) {
          const failedToken = (error.config?.headers?.Authorization as string | undefined)?.slice(
            'Token '.length
          );
          // 401 URL/method/status and failed token fingerprint before asking native
          authLog('401', {
            url: error.config?.url,
            method: error.config?.method,
            status: 401,
            failedTokenFp: fingerprint(failedToken),
          });
          let nativeOutcome = 'no_webview';
          if (window.ReactNativeWebView) {
            const { token } = await requestFromApp('GET_AUTH_TOKEN');
            // native token outcome: missing/timeout, same as failed, clearedToken match, or different
            if (!token) nativeOutcome = 'missing_or_timeout';
            else if (token === failedToken) nativeOutcome = 'same_as_failed';
            else if (token === clearedToken?.current) nativeOutcome = 'cleared_token_match';
            else nativeOutcome = 'recovered';
            authTrail('native token outcome', { outcome: nativeOutcome, nativeTokenFp: fingerprint(token) });
            if (nativeOutcome === 'recovered') {
              setAuth(token);
              return Promise.reject(error);
            }
          }
          // clears both cookie and native secure storage — record why we didn't recover
          authLog('auth:clear via 401', { url: error.config?.url, reason: nativeOutcome });
          setAuth(null);
        }
        return Promise.reject(error);
      }
    );

    apiInstance.event = async (event, flow, metadata) => {
      // event can be either a string or an array of strings
      const events = Array.isArray(event) ? event : [event];
      await events.reduce(async (previousPromise, e) => {
        await previousPromise;

        return apiInstance.post(`v1/events/`, {
          flow,
          event: e,
          ...(metadata && { metadata }),
        });
      }, Promise.resolve() as Promise<unknown>);
    };

    apiInstance.isGuest = isGuest;

    return apiInstance;
  }, [auth, lang, setAuth, isGuest, clearedToken]);
};

export default useAxios;
